import { FC, useCallback, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { QuestionnaireContext } from '../QuestionnaireContext'
import ActionButton from 'components/ActionButton'
import {
  ModalActions,
  ModalCard,
  ModalMessage,
  ModalOverlay,
  ModalTitle
} from 'components/modals/styles'

const Overlay = styled(ModalOverlay)`
  z-index: 10010;
`

const Card = styled(ModalCard)`
  width: 450px;
  max-width: 450px;
`

const Header = styled.div`
  margin-bottom: 10px;
`

const Body = styled.div`
  margin-bottom: 14px;
`

interface ClearModalProps {
  show?: boolean
  onClose?: () => void
  onConfirmClear?: () => void
}

const ClearModal: FC<ClearModalProps> = ({ show, onClose, onConfirmClear }) => {
  const { onClear, showClearModal, setShowClearModal } =
    useContext(QuestionnaireContext)

  /**
   * The assessment flow uses context-owned clear state, while assignments pass
   * local modal handlers directly. Resolve the effective modal controls here so
   * both screens share the same apo26 modal without duplicating markup.
   */
  const resolvedShow = show ?? showClearModal
  /**
   * Resolve the effective handlers once per dependency change so the escape-key
   * effect can depend on stable callback references instead of recreated
   * logical-expression results on every render.
   */
  const resolvedOnClose = useCallback(() => {
    if (onClose) {
      onClose()
      return
    }

    setShowClearModal(false)
  }, [onClose, setShowClearModal])

  const resolvedOnClear = useCallback(() => {
    if (onConfirmClear) {
      onConfirmClear()
      return
    }

    onClear()
  }, [onConfirmClear, onClear])

  const onClearAndClose = () => {
    resolvedOnClear()
    resolvedOnClose()
  }

  useEffect(() => {
    const handlePress = (event: any) => {
      if (event.keyCode === 27) {
        resolvedOnClose()
      }
    }

    resolvedShow && document.addEventListener('keydown', handlePress, false)

    return () => {
      document.removeEventListener('keydown', handlePress, false)
    }
  }, [resolvedShow, resolvedOnClose])

  if (!resolvedShow) return null

  return (
    <Overlay onClick={resolvedOnClose}>
      <Card
        role='dialog'
        aria-modal='true'
        aria-labelledby='clear-modal-title'
        onClick={(event) => event.stopPropagation()}
      >
        <Header>
          <ModalTitle id='clear-modal-title'>
            Clear the current question?
          </ModalTitle>
        </Header>
        <Body>
          <ModalMessage>
            You will lose all your current work for this question.
          </ModalMessage>
        </Body>
        <ModalActions>
          <ActionButton
            label='Cancel'
            variant='clear'
            width={92}
            onClick={resolvedOnClose}
          />
          <ActionButton
            label='Clear'
            variant='success'
            width={92}
            onClick={onClearAndClose}
          />
        </ModalActions>
      </Card>
    </Overlay>
  )
}

export default ClearModal
